import { BookOpen, Shield, Search, FileText, Users, Building2, Scale, Lock } from "lucide-react";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import MarketingCta from "../components/MarketingCta";
import Footer from "../components/Footer";

const CAPABILITIES = [
  {
    icon: BookOpen,
    title: "Collective Agreement Interpretation",
    desc: "Ask questions about your agreement in plain English and receive structured answers anchored in the actual contract text, with citations to specific articles and clauses.",
  },
  {
    icon: Scale,
    title: "Labour Relations Support",
    desc: "Clarify provisions on overtime, seniority, scheduling, discipline, leave, and union dues — consistently, across every person and department that relies on the agreement.",
  },
  {
    icon: Shield,
    title: "Grievance Management Assistance",
    desc: "Run risk-scored case reviews before action is taken. Understand grievance exposure, procedural requirements, and the clause language that applies to a scenario.",
  },
  {
    icon: Search,
    title: "Contract Search & Analysis",
    desc: "Agreement-specific indexing means every search and answer is grounded in your document — not generic internet knowledge.",
  },
  {
    icon: FileText,
    title: "Workplace Investigations",
    desc: "Check representation rights, procedural protections, and notice requirements that apply during investigations and disciplinary processes.",
  },
  {
    icon: Building2,
    title: "HR & Labour Relations Operations",
    desc: "Secure document storage, answer history, and exportable documentation give teams an accountable, repeatable workflow.",
  },
];

const AUDIENCES = [
  { title: "Union Representatives & Stewards", desc: "Answer member questions quickly and accurately, grounded in the agreement's exact language." },
  { title: "Labor Negotiators", desc: "Prepare for bargaining with clause-level analysis instead of manual page-by-page review." },
  { title: "HR & Labour Relations Leaders", desc: "Apply provisions consistently across managers and departments, and assess risk before acting." },
  { title: "Public Sector Locals & Organizations", desc: "Institutional-grade governance, access controls, and privacy for organizations of any size." },
];

const ABOUT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About CBAIQ",
  "url": "https://cbaiq.com/about",
  "description": "CBAIQ is AI-powered collective agreement interpretation software for union representatives, labor negotiators, and HR & labour relations professionals.",
  "mainEntity": {
    "@type": "Organization",
    "@id": "https://cbaiq.com/#organization",
    "name": "CBAIQ",
    "url": "https://cbaiq.com",
    "logo": "https://cbaiq.com/cbaiq-logo.png",
    "email": "support@cbaiq.com",
    "description": "CBAIQ builds AI-powered collective agreement interpretation software. It provides structured, citation-backed analysis of collective bargaining agreements for labour relations professionals on both the union and employer side.",
  },
};

export default function About() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="about-page">
      <Seo
        title="About CBAIQ | AI for Collective Agreements & Labour Relations"
        description="Learn what CBAIQ is, who it serves, and why it exists: AI-powered, citation-backed collective agreement interpretation built for union representatives, negotiators, and HR & labour relations teams."
        path="/about"
        jsonLd={ABOUT_JSONLD}
      />
      <PublicHeader />

      <div className="flex-1">
        {/* Hero */}
        <section className="pt-20 sm:pt-24 pb-14 px-4" data-testid="about-hero">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-4">About CBAIQ</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight tracking-tight text-foreground mb-6">
              Making Collective Agreements Understandable — For Both Sides of the Table
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              CBAIQ is AI-powered collective agreement interpretation software. It gives union representatives,
              labor negotiators, and HR &amp; labour relations professionals structured, citation-backed answers
              from their own agreements — so decisions are grounded in what the contract actually says.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-14 border-y border-border/40 bg-muted/20 px-4" data-testid="about-mission">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-5">Our Mission</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Collective agreements govern the working lives of millions of people, yet they are dense, technical,
              and interpreted inconsistently. A misread overtime clause, a missed procedural step, or an
              inconsistent application of seniority language can cost organizations grievances, arbitration, and
              trust between the parties.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              CBAIQ exists to close that gap. Our mission is to make every collective agreement instantly
              understandable to the people who work under it and administer it — with answers that are traceable
              to specific articles and language, never generic AI summaries.
            </p>
          </div>
        </section>

        {/* What CBAIQ does */}
        <section className="py-16 px-4" data-testid="about-capabilities">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-3">What CBAIQ Does</h2>
            <p className="text-sm text-muted-foreground max-w-lg mb-10">
              Purpose-built agreement intelligence for collective bargaining environments — not a general-purpose chatbot.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {CAPABILITIES.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="border border-border rounded-lg p-7" data-testid={`about-capability-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <Icon className="w-5 h-5 text-muted-foreground mb-4" />
                  <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who it serves */}
        <section className="py-16 border-y border-border/40 bg-muted/20 px-4" data-testid="about-audiences">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-10">Who Uses CBAIQ</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {AUDIENCES.map(({ title, desc }) => (
                <div key={title} className="border border-border rounded-lg p-6 bg-background">
                  <Users className="w-4 h-4 text-muted-foreground mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why AI helps */}
        <section className="py-16 px-4" data-testid="about-why-ai">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-5">Why AI Helps Labour Relations</h2>
            <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
              <p>
                Manual clause review is slow, and interpretation often varies between individuals. AI that is
                indexed to a specific agreement can retrieve the relevant articles in seconds and present them
                consistently — the same question gets the same grounded answer, every time.
              </p>
              <p>
                Unlike general AI tools, CBAIQ anchors every output in the uploaded agreement's text and cites
                the articles it relied on, so professionals can verify each answer against the source. Structured
                risk scoring adds a second layer of judgment support before action is taken.
              </p>
              <p>
                CBAIQ is an educational decision-support tool — it does not provide legal advice and does not
                replace labour relations professionals or legal counsel. It makes their work faster, more
                consistent, and easier to document.
              </p>
            </div>
          </div>
        </section>

        {/* Privacy strip */}
        <section className="py-12 border-t border-border/40 px-4" data-testid="about-privacy">
          <div className="max-w-3xl mx-auto flex items-start gap-4">
            <Lock className="w-5 h-5 text-muted-foreground mt-1 flex-shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Privacy-first architecture.</span> Uploaded agreements
              are never used for model training, are not externally indexed, and remain under organizational
              control with institutional-grade access handling.
            </p>
          </div>
        </section>

        <MarketingCta page="about" />
      </div>
      <Footer />
    </div>
  );
}
